import { combineEpics, ofType, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, startWith } from 'rxjs/operators';
import { requestActions } from './apiHelpers';
import AppState from '../reducers/app-state';
import { ID, ReduxAction } from '@repo/utils/types';
import { activityApiObservable } from '@repo/utils/helpers/api';
import { ActivityItemType } from '@repo/utils/models/Activity';
import { selectGroupMemberIDs } from './groups';

/* Epic actions */
const groupActivityActions = requestActions('GROUP_ACTIVITY', ['fetch']);

/* Component actions */
// Always fetches the first page and overwrites whatever is in state
export const fetchGroupActivity = (id: ID) => groupActivityActions['fetch']({ id });
// Handles pagination. Subsequent pages are merged into the already loaded feed.
export const fetchMoreGroupActivity = (id: ID) => ({
  type: 'FETCH_MORE_GROUP_ACTIVITY',
  payload: { id },
});

export const setGroupActivityFilter = (id: ID, filter: ActivityItemType) => ({
  type: 'SET_GROUP_ACTIVITY_FILTER',
  payload: { id, filter },
});

/* Observables/Epic helpers */
const fetchGroupActivityObservable = (params: {
  limit: number;
  types?: ActivityItemType[];
  after?: number;
  contacts?: ID[];
}) =>
  activityApiObservable('POST', 'activity/', { body: { ...params, status: 'all' } }).pipe(
    map((response) =>
      groupActivityActions['fetchSucceeded']({
        ...response,
        filter: params.types?.[0],
        resultsAfterDate: params.after,
      }),
    ),
    startWith(groupActivityActions['fetchRequested']({ filter: params.types?.[0] })),
    catchError((err) => of(groupActivityActions['fetchFailed'](err))),
  );

/* Epics */
const fetchGroupActivityEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_GROUP_ACTIVITY', 'SET_GROUP_ACTIVITY_FILTER', 'MERGE_CONTACT_BATCH_SUCCEEDED'),
    mergeMap(({ payload }) => {
      let types = [payload?.filter || state$.value.data.groupActivity.activeFilter].filter(Boolean);

      if (types.length === 0) {
        types = undefined;
      }

      return fetchGroupActivityObservable({
        limit: state$.value.data.groupActivity.all.limit,
        contacts: selectGroupMemberIDs(payload.id)(state$.value),
        types,
      });
    }),
  );

const fetchMoreGroupActivityEpic = (
  action$: Observable<ReduxAction>,
  state$: StateObservable<AppState>,
) =>
  action$.pipe(
    ofType('FETCH_MORE_GROUP_ACTIVITY'),
    mergeMap(({ payload }) => {
      let types = [state$.value.data.groupActivity.activeFilter].filter(Boolean);

      if (types.length === 0) {
        types = undefined;
      }

      return fetchGroupActivityObservable({
        limit: state$.value.data.groupActivity.all.limit,
        after: !!state$.value.data.groupActivity.activeFilter
          ? parseInt(state$.value.data.groupActivity.filtered.oldestItemDate)
          : parseInt(state$.value.data.groupActivity.all.oldestItemDate),
        contacts: selectGroupMemberIDs(payload.id)(state$.value),
        types,
      });
    }),
  );

export const groupActivityEpic = combineEpics(fetchGroupActivityEpic, fetchMoreGroupActivityEpic);
